/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useState } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useHistory, useLocation } from 'react-router-dom'
import Icon from '../../components/Icon'
import stripeLogo from './stripe_logo.svg'

import { loadStripe } from '@stripe/stripe-js'
import LoadingSpinner from '../../components/LoadingSpinner'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { useI18n } from '@shopify/react-i18n'

const PAYMENT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  PENDING: 'pending',
}

async function getDivision(params) {
  try {
    let response

    if (params?.divisionId) {
      response = await req(
        `/schedules/${params.divisionId}/registration/onboarding/team/${params.teamId}`
      )
    }

    if (params?.teamId && params?.rosterId && params?.billingId) {
      response = await req(
        `/teams/${params.teamId}/rosters/${params.rosterId}/billing/${params.billingId}`
      )
    }

    const division = response.data

    const secret = await createPaymentIntent(division)

    return { secret, division }
  } catch (e) {
    console.error(e)
  }
}

async function createPaymentIntent(division) {
  try {
    const clientSecret = await req('/payment/intent', {
      method: 'POST',
      body: JSON.stringify({
        billingScheduleId: division.billing[0].uid,
        paymentAmount: division.billing[0].amount * 100,
        divisionId: division.schedule_id,
        paymentCurrency: division.currency.id,
        teamId: division.team_id,
        teamRosterId: division.team_player_roster_id,
        teamRosterRegistrationId:
          division.team_player_roster_to_registration_id,
      }),
    })

    return clientSecret
  } catch (e) {
    console.error(e)
  }
}

const RegistrationPayment = () => {
  const [division, setDivision] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [paymentIsLoading, setPaymentLoad] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const history = useHistory()
  const [i18n] = useI18n()

  const { search } = useLocation()
  const params = useMemo(() => {
    const divisionRegex = /\?divisionId=([^&]+)&teamId=([^&]+)/
    const divisionMatch = divisionRegex.exec(search)

    if (divisionMatch) {
      const divisionId = divisionMatch[1]
      const teamId = divisionMatch[2]

      return {
        divisionId,
        teamId,
      }
    }

    const billingRegex = /\?teamId=([^&]+)&rosterId=([^&]+)&billingId=([^&]+)/
    const billingMatch = billingRegex.exec(search)

    if (billingMatch) {
      const teamId = billingMatch[1]
      const rosterId = billingMatch[2]
      const billingId = billingMatch[3]

      return {
        teamId,
        rosterId,
        billingId,
      }
    }
  }, [search])

  if (!params) {
    return (
      <div>
        <h1>Not Found. (Err. 17)</h1>
      </div>
    )
  }

  const bootstrap = useCallback(async () => {
    const { secret, division } = await getDivision(params)

    if (!secret || !division) {
      console.error('No secret or division found')
      return
    }

    let stripeClient = null

    const host = window.location.hostname || window.location.host
    if (
      __DEV__ ||
      !['app.sportninja.com', 'canlanstats.sportninja.com'].includes(host)
    ) {
      console.log('LOADING DEV')
      stripeClient = await loadStripe(process.env.REACT_APP_STRIPE_KEY_DEV)
    } else {
      console.log('LOADING PROD')
      stripeClient = await loadStripe(process.env.REACT_APP_STRIPE_KEY)
    }

    if (!stripeClient) {
      return
    }

    const elements = stripeClient.elements({
      clientSecret: secret,
      appearance: {
        theme: 'night',
      },
    })

    let paymentElement = elements.create('payment', {
      paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
    })

    const paymentDiv = document.querySelector('#payment-element')

    if (!paymentDiv) {
      return
    }

    paymentElement.mount('#payment-element')

    const stripeForm = document.querySelector('#payment-form')

    stripeForm.addEventListener('submit', async (e) => {
      e.preventDefault()
      setPaymentLoad(true)

      if (!stripeClient) {
        return
      }

      const { error } = await stripeClient.confirmPayment({
        elements,
        redirect: 'if_required',
      })

      if (error) {
        setPaymentLoad(false)
        setPaymentStatus(PAYMENT_STATUS.FAILED)
        setErrorMessage(error.message)
      } else {
        setPaymentLoad(false)
        setPaymentStatus(PAYMENT_STATUS.SUCCESS)
      }
    })

    setDivision(division)
    setLoaded(true)
  }, [params])

  useEffect(() => {
    bootstrap()
  }, [])

  return (
    <div
      css={css`
        display: flex;
        height: 100vh;
      `}
    >
      <div
        css={css`
          width: 50%;
          padding-top: 80px;
          padding-left: 106px;
          padding-right: 140px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        `}
      >
        <div
          onClick={() => history.push(ROUTES.HOME)}
          css={css`
            display: flex;
            align-items: center;

            &:hover {
              cursor: pointer;
            }
          `}
        >
          <Icon name='home' color={colors.DEFAULT_FLAIR} fontSize={12} />
          <p
            css={css`
              font-size: 12px;
              font-weight: 500;
              margin-left: 12px;
              color: ${colors.DEFAULT_FLAIR};
            `}
          >
            GO HOME
          </p>
        </div>

        <div>
          {division?.billing[0].amount ? (
            <>
              {division?.organization_image?.full_path && (
                <img
                  src={division?.organization_image?.full_path}
                  alt={division?.organization_name + ' League Logo'}
                  css={css`
                    margin-top: 32px;
                    width: 160px;
                    height: auto;
                    max-height: 160px;
                  `}
                />
              )}
              <div
                css={css`
                  margin-top: 56px;
                `}
              >
                <p
                  css={css`
                    font-weight: 700;
                    font-size: 18px;
                  `}
                >
                  Paying {division?.billing[0]?.payment_type}
                  {' to '}
                  {division?.organization_name
                    ? division?.organization_name
                    : 'SportNinja'}
                </p>
                <p
                  css={css`
                    font-weight: 700;
                    font-size: 40px;
                    margin-top: 14px;
                  `}
                >
                  {i18n.formatCurrency(division?.billing[0]?.amount, {
                    currency: division?.currency?.name,
                    form: 'short',
                  })}
                </p>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  margin-top: 56px;
                `}
              >
                {division?.root_schedule_image?.full_path && (
                  <img
                    src={division?.root_schedule_image?.full_path}
                    css={css`
                      width: 50px;
                      height: auto;
                      margin-right: 16px;
                    `}
                  />
                )}
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                  `}
                >
                  {`${division?.root_schedule_name} - ${division?.name}`}
                </p>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 56px;
                `}
              >
                <p
                  css={css`
                    font-size: 16px;
                    text-transform: capitalize;
                  `}
                >
                  {division?.billing.payment_type
                    ? division?.billing.payment_type
                    : 'Subtotal'}
                </p>
                <p
                  css={css`
                    font-size: 16px;
                  `}
                >
                  {i18n.formatCurrency(division?.billing[0]?.amount, {
                    currency: division?.currency?.name,
                    form: 'explicit',
                  })}
                </p>
              </div>
              {/* 
              <button
                css={css`
                  color: ${colors.DEFAULT_FLAIR};
                  margin: 20px 0;
                  font-size: 16px;
                  font-weight: 500;
                  font-family: 'MaisonNeue', sans-serif;
                `}
                onClick={() => {}}
              >
                Add promotion code
              </button> */}

              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 16px;
                  padding-top: 16px;
                  border-top: 1px solid ${colors.SOFT_STEEL};
                `}
              >
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                  `}
                >
                  Total due
                </p>
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                  `}
                >
                  {i18n.formatCurrency(division?.billing[0]?.amount, {
                    currency: division?.currency?.name,
                    form: 'explicit',
                  })}
                </p>
              </div>
            </>
          ) : (
            <LoadingSpinner />
          )}
        </div>
        <div
          css={css`
            padding-bottom: 106px;
            display: flex;
            align-items: center;
          `}
        >
          <p>Powered by</p>
          <img
            src={stripeLogo}
            css={css`
              margin-left: 6px;
            `}
          />
        </div>
      </div>

      <div
        css={css`
          height: 100%;
          width: 1px;
          background-color: ${colors.SOFT_STEEL};
        `}
      />

      <div
        css={css`
          width: 50%;
          padding-top: 80px;
          padding-left: 106px;
          padding-right: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            margin-top: 32px;
            width: 100%;
          `}
        >
          {paymentStatus === PAYMENT_STATUS.SUCCESS ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name='check' color={colors.DEFAULT_FLAIR} fontSize={48} />
              <p
                css={css`
                  margin-top: 32px;
                  font-size: 24px;
                  font-weight: 700;
                `}
              >
                Payment Successful
              </p>
              <button
                onClick={() => history.push(ROUTES.HOME)}
                css={css`
                  margin-top: 100px;
                  display: flex;
                  flex-direction: row;
                  justify-content: center;
                  align-items: center;
                  padding: 8px 24px;
                  border: 2px solid #f2b400;
                  border-radius: 4px;
                  height: 48px;
                  width: 100%;
                  color: ${colors.WHITE};
                `}
              >
                GO BACK TO HOME
              </button>
            </div>
          ) : paymentStatus === PAYMENT_STATUS.FAILED ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name='times' color={colors.ERROR} fontSize={48} />
              <p
                css={css`
                  margin-top: 32px;
                  font-size: 24px;
                  font-weight: 700;
                `}
              >
                Payment Failed
              </p>
              <span
                css={css`
                  margin-top: 20px;
                `}
              >
                {errorMessage}
              </span>
              <button
                onClick={() => window.location.reload()}
                css={css`
                  margin-top: 100px;
                  display: flex;
                  flex-direction: row;
                  justify-content: center;
                  align-items: center;
                  padding: 8px 24px;
                  border: 2px solid #f2b400;
                  border-radius: 4px;
                  height: 48px;
                  width: 100%;
                  color: ${colors.WHITE};
                `}
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div
                css={css`
                  visibility: ${loaded ? 'visible' : 'hidden'};
                `}
              >
                <form id='payment-form'>
                  <div id='card-element' />
                  <div id='payment-element' />
                  <button
                    disabled={paymentIsLoading}
                    id='submit'
                    css={css`
                      margin-top: 32px;
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      padding: 8px 24px;
                      border: 2px solid #f2b400;
                      border-radius: 4px;
                      height: 48px;
                      width: 100%;
                      color: ${colors.WHITE};
                    `}
                  >
                    {paymentIsLoading ? <LoadingSpinner size={2} /> : 'PAY NOW'}
                  </button>
                </form>
              </div>
              <div
                css={css`
                  visibility: ${loaded ? 'hidden' : 'visible'};
                `}
              >
                <LoadingSpinner />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RegistrationPayment
